import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'

export const NEW_SLOT = { startTime: '09:00:00', endTime: '20:00:00' }
export const DEFAULT_CLINIC_TIMEZONE = '+05:30'

export const WEEK_DAY_ORDER = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
]

export const DEFAULT_WEEKLY_HOUR_MAP = {
  sunday: getDefaultWeeklyHours('Sunday', false),
  monday: getDefaultWeeklyHours('Monday'),
  tuesday: getDefaultWeeklyHours('Tuesday'),
  wednesday: getDefaultWeeklyHours('Wednesday'),
  thursday: getDefaultWeeklyHours('Thursday'),
  friday: getDefaultWeeklyHours('Friday'),
  saturday: getDefaultWeeklyHours('Saturday', false),
}

export function getDefaultWeeklyHours(title, available = true) {
  return { available: available, title: title, slotList: available ? [NEW_SLOT] : null }
}

export const NEW_SCHEDULE_OBJECT = {
  id: `clinic_schedule-${uuidv4()}`,
  title: 'Clinic Schedule',
  defaultSchedule: true,
  timeZone: encodeURIComponent(DEFAULT_CLINIC_TIMEZONE),
  weeklyHourMap: DEFAULT_WEEKLY_HOUR_MAP,
}

function safeDecodeURIComponent(value) {
  if (!value || typeof value !== 'string') {
    return value
  }
  try {
    return decodeURIComponent(value)
  } catch (error) {
    return value
  }
}

export function getClinicScheduleTimezoneOffset(clinicObject) {
  const scheduleTimeZone = clinicObject?.schedules?.find((schedule) => schedule?.timeZone)?.timeZone
  const rawTimeZone =
    clinicObject?.timeZone?.offset ||
    clinicObject?.timeZone?.gmtvalue ||
    clinicObject?.timeZone ||
    clinicObject?.timezone ||
    scheduleTimeZone ||
    DEFAULT_CLINIC_TIMEZONE

  if (typeof rawTimeZone === 'object') {
    return rawTimeZone?.offset || rawTimeZone?.gmtvalue || DEFAULT_CLINIC_TIMEZONE
  }

  return safeDecodeURIComponent(rawTimeZone) || DEFAULT_CLINIC_TIMEZONE
}

export function createDefaultWeeklyHourMap() {
  return WEEK_DAY_ORDER.reduce((acc, dayKey) => {
    const defaultDay = DEFAULT_WEEKLY_HOUR_MAP[dayKey]
    acc[dayKey] = {
      ...defaultDay,
      slotList: defaultDay?.available ? [{ ...NEW_SLOT }] : null,
    }
    return acc
  }, {})
}

export function createDefaultClinicSchedule(
  timeZone = DEFAULT_CLINIC_TIMEZONE,
  title = 'Clinic Schedule',
) {
  return {
    id: `clinic_schedule-${uuidv4()}`,
    title,
    defaultSchedule: true,
    timeZone: encodeURIComponent(safeDecodeURIComponent(timeZone) || DEFAULT_CLINIC_TIMEZONE),
    weeklyHourMap: createDefaultWeeklyHourMap(),
  }
}

function normalizeSlot(slot) {
  return {
    startTime: slot?.startTime || NEW_SLOT.startTime,
    endTime: slot?.endTime || NEW_SLOT.endTime,
  }
}

function normalizeWeeklyHourMap(weeklyHourMap = {}) {
  return WEEK_DAY_ORDER.reduce((acc, dayKey) => {
    const defaultDay = DEFAULT_WEEKLY_HOUR_MAP[dayKey]
    const daySchedule = weeklyHourMap?.[dayKey] || weeklyHourMap?.[dayKey.toUpperCase()] || {}
    const available =
      typeof daySchedule?.available === 'boolean' ? daySchedule.available : defaultDay.available
    const slotList = Array.isArray(daySchedule?.slotList)
      ? daySchedule.slotList.map(normalizeSlot)
      : available
        ? [{ ...NEW_SLOT }]
        : null

    acc[dayKey] = {
      ...defaultDay,
      ...daySchedule,
      title: daySchedule?.title || defaultDay.title,
      available,
      slotList,
    }
    return acc
  }, {})
}

export function normalizeSchedule(schedule, index = 0, fallbackTimeZone = DEFAULT_CLINIC_TIMEZONE) {
  const defaultSchedule = createDefaultClinicSchedule(fallbackTimeZone)
  return {
    ...defaultSchedule,
    ...(schedule || {}),
    id: schedule?.id || defaultSchedule.id,
    title: schedule?.title || (index === 0 ? 'Clinic Schedule' : `Clinic Schedule ${index + 1}`),
    defaultSchedule: Boolean(schedule?.defaultSchedule),
    timeZone: encodeURIComponent(
      safeDecodeURIComponent(schedule?.timeZone || schedule?.timezone || fallbackTimeZone) ||
        DEFAULT_CLINIC_TIMEZONE,
    ),
    weeklyHourMap: normalizeWeeklyHourMap(schedule?.weeklyHourMap),
  }
}

export function normalizeClinicScheduleForm(clinicObject = {}) {
  const fallbackTimeZone = getClinicScheduleTimezoneOffset(clinicObject)
  const sourceSchedules = Array.isArray(clinicObject?.schedules) ? clinicObject.schedules : []
  const schedules =
    sourceSchedules.length > 0
      ? sourceSchedules.map((schedule, index) =>
          normalizeSchedule(schedule, index, fallbackTimeZone),
        )
      : [createDefaultClinicSchedule(fallbackTimeZone)]

  const hasDefaultSchedule = schedules.some((schedule) => schedule.defaultSchedule)
  return {
    ...clinicObject,
    schedules: schedules.map((schedule, index) => ({
      ...schedule,
      defaultSchedule: hasDefaultSchedule ? Boolean(schedule.defaultSchedule) : index === 0,
    })),
  }
}

export function validateClinicFunction(clinicObject) {
  const normalizedClinic = normalizeClinicScheduleForm(clinicObject)
  const schedules = normalizedClinic.schedules || []
  const schedulesValidation = schedules.map((schedule) => {
    const weeklyHourMap = schedule.weeklyHourMap
    let hasAvailableSlot = false
    const validateWeeklyHourMap = Object.entries(weeklyHourMap || {}).every(
      ([_dayKey, daySchedule]) => {
        if (!daySchedule.available) {
          return true
        }
        if (!Array.isArray(daySchedule?.slotList) || daySchedule.slotList.length === 0) {
          return false
        }
        const validateSlotList = (daySchedule?.slotList || [])?.every((slot, idx) => {
          const startTimeEndTimeNoConflict =
            dayjs(slot.startTime, 'HH:mm:ss') < dayjs(slot.endTime, 'HH:mm:ss')
          var overLapPrev = false
          if (idx !== 0) {
            overLapPrev =
              dayjs(slot.startTime, 'HH:mm:ss') <
              dayjs(daySchedule?.slotList?.[idx - 1].endTime, 'HH:mm:ss')
          }
          if (startTimeEndTimeNoConflict && !overLapPrev) {
            hasAvailableSlot = true
          }
          return startTimeEndTimeNoConflict && !overLapPrev
        })

        return validateSlotList
      },
    )

    return validateWeeklyHourMap && hasAvailableSlot
  })
  if (!schedulesValidation.every((sch) => sch)) {
    toast.error('Please fix the highlighted schedule before saving.')
  }
  return {
    schedules: schedulesValidation.every((sch) => sch),
    schedulesValidationArray: schedulesValidation,
  }
}
